import { IconBrandTiktok, IconExternalLink } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import type { RamalamaTikTokDemo } from './ramalama-copy';

/** Size of TikTok's official v2 embed player for a vertical post. */
const EMBED_WIDTH = 325;
const EMBED_HEIGHT = 740;

/**
 * One TikTok post through TikTok's official embed player
 * (`tiktok.com/embed/v2/<id>`), the iframe that TikTok's own embed code
 * renders. Nothing from TikTok loads until the card is near the viewport;
 * before that the card shows the credit and a link, at the player's size
 * so nothing shifts when the iframe arrives.
 */
function TikTokEmbed({ demo }: { demo: RamalamaTikTokDemo }) {
  const ref = useRef<HTMLDivElement>(null);
  const [load, setLoad] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || load) return;
    if (typeof IntersectionObserver === 'undefined') {
      setLoad(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [load]);

  return (
    <figure className="flex min-w-0 flex-col gap-3">
      <div
        ref={ref}
        className="relative mx-auto w-full max-w-[325px] overflow-hidden rounded-xl border border-white/10 bg-white/[0.03]"
        style={{ aspectRatio: `${EMBED_WIDTH} / ${EMBED_HEIGHT}` }}
        data-tiktok-embed={demo.id}
        data-loaded={load ? 'true' : 'false'}
      >
        {load ? (
          <iframe
            src={`https://www.tiktok.com/embed/v2/${demo.id}`}
            title={`TikTok video by @${demo.author}: ${demo.title}`}
            loading="lazy"
            allow="encrypted-media; fullscreen; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 size-full border-0"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center text-sm text-white/55">
            <IconBrandTiktok className="size-8 text-white/40" aria-hidden />
            <span>Loading the TikTok by @{demo.author}…</span>
          </div>
        )}
      </div>
      <figcaption className="mx-auto w-full max-w-[325px] text-sm leading-6">
        <span className="block font-semibold text-white">{demo.title}</span>
        <span className="block text-white/65">{demo.caption}</span>
        <a
          href={demo.url}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="mt-1 inline-flex items-center gap-1 text-[#7ee35f] underline-offset-4 hover:underline"
        >
          @{demo.author} on TikTok
          <IconExternalLink className="size-3.5" aria-hidden />
        </a>
      </figcaption>
    </figure>
  );
}

export function TikTokEmbeds({ items }: { items: RamalamaTikTokDemo[] }) {
  return (
    <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((demo) => (
        <TikTokEmbed key={demo.id} demo={demo} />
      ))}
    </div>
  );
}
